import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { v1Get, v1Patch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StarRating } from '@/components/StarRating';
import { FINAL_CALL_OPTIONS, resolveScore } from '@/lib/reportConstants';
import { toastMutationError } from '@/lib/resumeEnrichmentErrors';
import { toast } from 'sonner';
import { useHasPermission } from '@/auth/permissions';

const RATING_MIN = 0;
const RATING_MAX = 5;

function clampRating(raw) {
  if (raw === '' || raw == null) return null;
  const n = Number(raw);
  if (Number.isNaN(n)) return null;
  return Math.min(RATING_MAX, Math.max(RATING_MIN, n));
}

function ScoreInput({ label, value, onChange, testId, disabled = false }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-[13px] font-medium">{label}</span>
      <div className="flex items-center gap-2">
        <StarRating score={value} />
        <Input
          type="number"
          min={RATING_MIN}
          max={RATING_MAX}
          step={0.5}
          className="h-8 w-20"
          value={value ?? ''}
          onChange={(e) => onChange(clampRating(e.target.value))}
          onInvalid={(e) => e.preventDefault()}
          data-testid={testId}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

/** Per video-bot response human star override. */
export function VideoBotHumanRatingRow({ candidateId, response, readOnly = false }) {
  const qc = useQueryClient();
  const draft = resolveScore(response.human_overall_score, response.overall_score);
  const [score, setScore] = useState(draft);
  const mut = useMutation({
    mutationFn: () => v1Patch(
      `/candidates/${candidateId}/videobot/responses/${response.id}`,
      { human_overall_score: clampRating(score), client_curated: response.client_curated },
    ),
    onSuccess: () => {
      toast.success('Video Bot rating saved');
      qc.invalidateQueries({ queryKey: ['v1-candidate', candidateId] });
      qc.invalidateQueries({ queryKey: ['v1-client-report-preview', candidateId] });
    },
    onError: (e) => toastMutationError(e, 'Save failed'),
  });
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-secondary/20 px-3 py-2" data-testid={`vb-human-rating-${response.id}`}>
      <div className="min-w-0 flex-1">
        <p className="text-[12px] text-muted-foreground mb-1">Human rating</p>
        <ScoreInput
          label="Score"
          value={score}
          onChange={readOnly ? () => {} : setScore}
          testId={`vb-score-${response.id}`}
          disabled={readOnly}
        />
      </div>
      {!readOnly && (
        <Button size="sm" onClick={() => mut.mutate()} disabled={mut.isPending || score == null} data-testid={`vb-save-rating-${response.id}`}>
          {mut.isPending ? 'Saving…' : 'Save rating'}
        </Button>
      )}
    </div>
  );
}

/**
 * Slim staff-only "Final call" selector for the HR Offer stage.
 *
 * Behavioural scores now come from the panellist feedback form (aggregated into
 * HrEvaluationRating). Final call remains an explicit staff decision that drives
 * the staff Recommendation banner, so it saves via a dedicated endpoint that
 * does NOT touch the aggregated scores.
 */
export function HrFinalCallCard({ candidateId, evaluationId }) {
  const qc = useQueryClient();
  const canEditEvaluation = useHasPermission('evaluation.edit');
  const { data, isLoading } = useQuery({
    queryKey: ['hr-evaluation-ratings', candidateId, evaluationId],
    queryFn: () => v1Get(`/candidates/${candidateId}/hr-evaluation-ratings?evaluation_id=${evaluationId}`),
    enabled: !!candidateId && !!evaluationId,
  });
  const [finalCall, setFinalCall] = useState('');

  React.useEffect(() => {
    setFinalCall(data?.final_call || '');
  }, [data]);

  const mut = useMutation({
    mutationFn: () => v1Patch(`/candidates/${candidateId}/hr-evaluation-ratings/final-call`, {
      evaluation_id: evaluationId,
      final_call: finalCall || null,
    }),
    onSuccess: () => {
      toast.success('Final call saved');
      qc.invalidateQueries({ queryKey: ['hr-evaluation-ratings', candidateId, evaluationId] });
      qc.invalidateQueries({ queryKey: ['v1-candidate', candidateId] });
      qc.invalidateQueries({ queryKey: ['v1-client-report-preview', candidateId] });
    },
    onError: (e) => toastMutationError(e, 'Save failed'),
  });

  if (!evaluationId) {
    return (
      <Card>
        <CardContent className="p-4 text-[13px] text-muted-foreground">
          Select an evaluation to set the HR Final call.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="hr-final-call-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">HR Final call</CardTitle>
        <p className="text-[12px] text-muted-foreground">
        Saving the final call completes the Offer stage and unlocks the Generate Recommendation feature. This stays internal — it's never shown on shared client links. Behavioural
          ratings come from the HR interview and panellist feedback.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="space-y-1.5">
          <label className="text-[13px] font-medium" htmlFor="hr-final-call">
            Final call
          </label>
          <Select
            value={finalCall || undefined}
            onValueChange={setFinalCall}
            disabled={!canEditEvaluation}
          >
            <SelectTrigger id="hr-final-call" data-testid="hr-final-call" className="w-full">
              <SelectValue placeholder="Select final call…" />
            </SelectTrigger>
            <SelectContent>
              {FINAL_CALL_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} data-testid={`hr-final-call-${opt.value}`}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!isLoading && !data?.completed && (
            <p className="text-[12px] text-amber-700" data-testid="hr-final-call-pending-hint">
              Complete the HR interview and all panellist feedback before saving Final Call.
            </p>
          )}
          {!isLoading && data?.completed && data?.final_call && !data?.final_call_current && (
            <p className="text-[12px] text-amber-700" data-testid="hr-final-call-stale-hint">
              This Final Call was saved for an earlier HR interview. Save it again to
              complete the Offer stage.
            </p>
          )}
        </div>
        {canEditEvaluation && (
        <div className="flex justify-end pt-2">
          <Button
            size="sm"
            disabled={mut.isPending || isLoading || !data?.completed || !finalCall || !evaluationId}
            onClick={() => mut.mutate()}
            data-testid="hr-final-call-save"
          >
            {mut.isPending ? 'Saving…' : 'Save Final call'}
          </Button>
        </div>
        )}
      </CardContent>
    </Card>
  );
}
